import React from 'react';
import { Box, makeStyles } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { ReactComponent as Step1ActiveIcon } from './icons/step1-active.svg';
import { ReactComponent as Step1InactiveIcon } from './icons/step1-inactive.svg';
import { ReactComponent as Step2ActiveIcon } from './icons/step2-active.svg';
import { ReactComponent as Step2InactiveIcon } from './icons/step2-inactive.svg';
import { ReactComponent as Step3ActiveIcon } from './icons/step3-active.svg';
import { ReactComponent as Step3InactiveIcon } from './icons/step3-inactive.svg';
import { ReactComponent as Step4ActiveIcon } from './icons/step4-active.svg';
import { ReactComponent as Step4InactiveIcon } from './icons/step4-inactive.svg';
import bgLogo from './Kopie von RR-LOGO.png';
import vectorImg from './Vector.png';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(8, 0, 2),
    marginBottom: theme.spacing(0),
  },
  stepsBlock: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    width: '100%',
  },
  stepsRowWrap: {
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(3),
    position: 'relative',
    minWidth: 0,
    width: '100%',
    maxWidth: 800,
    margin: '0 auto',
    [theme.breakpoints.down('sm')]: {
      gap: theme.spacing(2),
    },
  },
  step: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    minWidth: 80,
    zIndex: 1,
    width: '25%',
  },
  iconWrapper: {
    '--step-accent': theme.palette.secondary.main,
    color: theme.palette.secondary.main,
    width: 64,
    height: 64,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: theme.spacing(1),
    '& svg': {
      width: '100%',
      height: '100%',
      display: 'block',
    },
    [theme.breakpoints.down('sm')]: {
      width: 48,
      height: 48,
    },
  },
  connector: {
    position: 'absolute',
    left: 'calc(12.5% + 20px)',
    right: 'calc(12.5% + 20px)',
    top: 32,
    height: 2,
    backgroundColor: '#000000',
    [theme.breakpoints.down('sm')]: {
      left: 'calc(12.5% + 14px)',
      right: 'calc(12.5% + 14px)',
      top: 24,
    },
  },
  label: {
    fontFamily: 'var(--font-family)',
    fontSize: 14,
    fontWeight: 600,
    textAlign: 'center',
    letterSpacing: '0.08em',
    color: theme.palette.secondary.main,
    [theme.breakpoints.down('sm')]: {
      fontSize: 12,
      height: 36,
    },
  },
  decorImgTopRight: {
    position: 'absolute',
    top: 0,
    right: 0,
    maxWidth: 400,
    width: '20%',
    height: 'auto',
    objectFit: 'contain',
    pointerEvents: 'none',
  },
  decorImgBottomLeft: {
    position: 'absolute',
    left: 0,
    top: 270,
    maxWidth: 300,
    width: '20%',
    height: 'auto',
    objectFit: 'contain',
    pointerEvents: 'none',
  },
}));

interface StepsProps {
  currentStep: 1 | 2 | 3 | 4;
}

interface StepConfig {
  id: 1 | 2 | 3 | 4;
  labelKey: string;
  activeIcon: React.ReactNode;
  inactiveIcon: React.ReactNode;
}

export default function Steps({ currentStep }: StepsProps) {
  const classes = useStyles();
  const { t } = useTranslation();

  const steps: StepConfig[] = [
    {
      id: 1,
      labelKey: 'Steps - Choose product',
      activeIcon: <Step1ActiveIcon />,
      inactiveIcon: <Step1InactiveIcon />,
    },
    {
      id: 2,
      labelKey: 'Steps - Choose repair',
      activeIcon: <Step2ActiveIcon />,
      inactiveIcon: <Step2InactiveIcon />,
    },
    {
      id: 3,
      labelKey: 'Steps - Delivery',
      activeIcon: <Step3ActiveIcon />,
      inactiveIcon: <Step3InactiveIcon />,
    },
    {
      id: 4,
      labelKey: 'Steps - Payment',
      activeIcon: <Step4ActiveIcon />,
      inactiveIcon: <Step4InactiveIcon />,
    },
  ];

  return (
    <Box className={classes.root} aria-hidden="true">
      <img src={bgLogo} alt="" className={classes.decorImgTopRight} aria-hidden />
      <img src={vectorImg} alt="" className={classes.decorImgBottomLeft} aria-hidden />
      <Box className={classes.stepsBlock}>
        <div className={classes.stepsRowWrap}>
          <Box className={classes.row}>
            <div className={classes.connector} />
            {steps.map((step) => {
              const isDone = step.id < currentStep;
              const isActive = step.id === currentStep;
              const showActiveIcon = isDone || isActive;

              return (
                <React.Fragment key={step.id}>
                  <div className={classes.step}>
                    <div className={classes.iconWrapper}>
                      {showActiveIcon ? step.activeIcon : step.inactiveIcon}
                    </div>
                    <div className={classes.label}>{t(step.labelKey)}</div>
                  </div>
                </React.Fragment>
              );
            })}
          </Box>
        </div>
      </Box>
    </Box>
  );
}

